<template>
  <form action="">
    <div class="modal-card roundTable">
      <b-loading :is-full-page="false" :active="isLoading" />

      <header class="modal-card-head">
        <p class="modal-card-title">
          <strong>Round {{ round.roundNumber }}:</strong>

          <span>
            {{ round.startTime | asDate }} {{ round.startTime | asTime }}
          </span>

          <span
            v-if="round.finishTime"
            :class="round.finishTime < round.startTime ? 'has-text-danger' : ''"
          >
            - {{ round.finishTime | asTime }}
          </span>

          <span v-if="elapsed">({{ elapsed | humanizeSeconds(false) }})</span>
        </p>

        <button type="button" class="delete" @click="$emit('close')" />
      </header>

      <section class="modal-card-body p-3">
        <TournamentGameStats :gameStats="gameStats" />

        <!-- Unpaired player list -->

        <div class="">
          <div class="is-flex is-align-items-center m-2">
            <p class="is-size-5 has-text-left has-text-info mr-2">
              UNPAIRED PLAYERS: {{ unpairedPlayers.length }}/{{
                players.length
              }}
            </p>

            <PlayersProgressBar
              class="is-flex-grow-1"
              :playersStats="playersStats"
            />
          </div>

          <PlayerTags
            :players="unpairedPlayers"
            :roundNumber="round.roundNumber"
            :totalRounds="tournament.totalRounds"
            :canClose="false"
            :hideAbsent="true"
            v-on="$listeners"
          />
        </div>

        <!-- Games Table -->
        <div class="table-row">
          <div class="table-cell">Board</div>
          <div class="table-cell">Status</div>
          <div class="table-cell">White</div>
          <div class="table-cell">Swap</div>
          <div class="table-cell">Black</div>
        </div>

        <RecycleScroller
          class="scroller-table"
          :items="roundGames"
          :item-size="60"
          key-field="matchGUID"
          v-slot="{ item: game }"
        >
          <div class="table-row">
            <!-- BOARD -->
            <div class="table-cell">
              <router-link
                v-if="game.matchRef"
                :to="getTournamentGameUrl(tournament.id, game.matchGUID)"
              >
                {{ game.board }}
              </router-link>
              <span v-else>{{ game.board }}</span>
            </div>

            <!-- STATUS -->
            <div
              class="table-cell"
              @mouseover="
                hoverId = game.matchRef ? game.matchRef.match_id : null
              "
              @mouseleave="hoverId = null"
            >
              <span
                v-if="game.matchRef"
                :class="
                  hasResultMismatch(game)
                    ? 'has-text-danger'
                    : hasPlayerMismatch(game)
                    ? 'has-background-warning'
                    : 'has-text-info'
                "
              >
                <span v-show="hoverId != game.matchRef.match_id">
                  {{ game.matchRef | matchResultText }}
                </span>
                <button
                  v-show="hoverId === game.matchRef.match_id"
                  @click="rematchGame(game)"
                >
                  Rematch
                </button>
              </span>
              <span
                v-else-if="
                  game.blackUsername &&
                  game.whiteUsername &&
                  game.blackResult === TOURNAMENT_GAME_RESULT.UNFINISHED &&
                  game.whiteResult === TOURNAMENT_GAME_RESULT.UNFINISHED
                "
              >
                <button @click="startGame(game)">Start</button>
              </span>
              <span v-else>no match</span>
            </div>

            <!-- WHITE -->
            <div class="table-cell">
              <div
                v-if="game.whiteUsername"
                class="is-flex is-justify-content-space-between"
              >
                <PlayerTag
                  :player="findPlayer(game.whiteUsername)"
                  :roundNumber="round.roundNumber"
                  :totalRounds="tournament.totalRounds"
                  :canClose="true"
                  @close="setGamePlayers(game, null, game.blackUsername)"
                />
                <TournamentResultSelect
                  :result="game.whiteResult"
                  :board-number="game.board"
                  color="white"
                  :is-two-player-match="
                    !!(game.whiteUsername && game.blackUsername)
                  "
                  @update:result="
                    (val) => {
                      game.whiteResult = val;
                    }
                  "
                />
              </div>
              <div v-else>
                <TournamentPairingPlayerSelect
                  :available-unpaired-players="availableUnpairedPlayers"
                  :board-number="game.board"
                  :color="'white'"
                  :opponent-player="findPlayer(game.blackUsername)"
                  :games="games"
                  :round-number="round.roundNumber"
                  @update:player="
                    (value) => setGamePlayers(game, value, game.blackUsername)
                  "
                />
              </div>
            </div>

            <!-- Swap position button -->
            <div class="table-cell">
              <ButtonTooltip
                v-show="game.whiteUsername && game.blackUsername"
                tooltip="Swap player positions"
                icon-left="retweet"
                type="is-default"
                @click="
                  setGamePlayers(game, game.blackUsername, game.whiteUsername)
                "
                :disabled="game.matchRef"
              />
            </div>

            <!-- BLACK -->
            <div class="table-cell">
              <div
                v-if="game.blackUsername"
                class="is-flex is-justify-content-space-between"
              >
                <PlayerTag
                  :player="findPlayer(game.blackUsername)"
                  :roundNumber="round.roundNumber"
                  :totalRounds="tournament.totalRounds"
                  :canClose="true"
                  @close="setGamePlayers(game, game.whiteUsername, null)"
                />
                <TournamentResultSelect
                  :result="game.blackResult"
                  :board-number="game.board"
                  color="black"
                  :is-two-player-match="
                    !!(game.whiteUsername && game.blackUsername)
                  "
                  @update:result="
                    (val) => {
                      game.blackResult = val;
                    }
                  "
                />
              </div>
              <div v-else>
                <TournamentPairingPlayerSelect
                  v-show="game.whiteUsername && availableUnpairedPlayers.length"
                  :available-unpaired-players="availableUnpairedPlayers"
                  :board-number="game.board"
                  :color="'black'"
                  :opponent-player="findPlayer(game.whiteUsername)"
                  :games="games"
                  :round-number="round.roundNumber"
                  @update:player="
                    (value) => setGamePlayers(game, game.whiteUsername, value)
                  "
                />
              </div>
            </div>
          </div>
        </RecycleScroller>

        <div class="table-row p-2">
          <button class="button is-small is-primary" @click="addBoard">
            Add board
          </button>
        </div>
      </section>

      <footer class="modal-card-foot">
        <b-button label="Simulation" type="is-default" @click="simulate" />

        <b-button label="Copy" type="is-default" @click="copyRound" />

        <b-button label="Abort" type="is-danger" @click="abortRound" />

        <b-button label="Reset" type="is-dark" @click="resetRound" />

        <b-button
          :label="`Pair (${tournament.type})`"
          type="is-primary"
          @click="handlePair"
        />

        <b-button label="Save" type="is-info" @click="saveRound" />

        <b-button
          label="Start Games"
          type="is-success"
          @click="$emit('onStart', round.roundNumber)"
        />

        <b-button
          label="Fix Results"
          type="is-warning"
          :disabled="
            elapsed < tournament.baseTimeControl * 60 && gameStats.live > 0
          "
          @click="$emit('onFinalize', round.roundNumber)"
        />

        <b-button
          v-if="
            !round.finishTime && tournament.currentRound === round.roundNumber
          "
          label="Finish Round"
          type="is-danger"
          :disabled="
            elapsed < tournament.baseTimeControl * 2 * 60 || gameStats.live > 0
          "
          @click="$emit('onFinish', round.roundNumber)"
        />

        <b-button
          v-if="
            round.finishTime && tournament.currentRound === round.roundNumber
          "
          label="Unfinish Round"
          type="is-danger"
          @click="$emit('onUnfinish', round.roundNumber)"
        />

        <div v-if="gameStats.live" class="notification is-success is-light">
          <p>
            <b>{{ gameStats.live }}</b>
            ongoing games
          </p>
        </div>

        <div v-else class="notification is-danger is-light">
          <p>
            <b>Save</b>
            to update standings
          </p>
        </div>
      </footer>
    </div>
  </form>
</template>

<script>
import PlayerTags from "@/components/admins/PlayerTags.vue";
import PlayerTag from "@/components/admins/PlayerTag.vue";
import PlayersProgressBar from "@/components/tournaments/PlayersProgressBar.vue";
import TournamentGameStats from "@/components/admins/tournaments/TournamentGameStats.vue";
import TournamentGameStatsLogic from "@/components/admins/tournaments/TournamentGameStatsLogic.js";
import GameHelper from "@/utils/game-helper";
import { uuidv4 } from "vue-chess-shared/utils";
import { firestore, functions } from "@/services/firebase";
import { memoize } from "lodash";
import {
  initBoards,
  newBoard,
  pairRound,
  setGamePlayers,
  getRoundPlayers,
  getScoreBeforeGame,
  isPlayerInRound,
  hasResultMismatch,
  saveRound,
  saveRoundGame,
  startRoundGame,
} from "vue-chess-shared/tournament/helper";
import { getStandings } from "vue-chess-shared/tournament/manager";
import {
  TOURNAMENT_GAME_RESULT,
  TOURNAMENT_GAME_RESULT_TEXT,
  TOURNAMENT_GAME_RESULT_SCORE,
} from "vue-chess-shared/constants";
import gameUrls from "@/mixins/gameUrls";
import { serverTimestamp } from "@/utils/firebase-helper";
import {
  getPlayedColors,
  getPlayedColorPref,
} from "vue-chess-shared/tournament/helper";
import ButtonTooltip from "@/components/ui/ButtonTooltip.vue";
import TournamentResultSelect from "@/components/admins/TournamentResultSelect.vue";
import { RecycleScroller } from "vue-virtual-scroller";
import "vue-virtual-scroller/dist/vue-virtual-scroller.css";
import TournamentPairingPlayerSelect from "@/components/admins/TournamentPairingPlayerSelect.vue";
import MatchHelper from "@/utils/match-helper";
import { isAiPlayer } from "vue-chess-shared/utils/ai-helper";
import { GAME_OVERRIDE } from "vue-chess-shared";
import { getJSONTournamentAtRound } from "vue-chess-shared/tournament/utils/tournamentUtils";

const handleBBPPair = functions.httpsCallable("tournament-handleBBPPair");

export default {
  components: {
    PlayerTags,
    PlayerTag,
    PlayersProgressBar,
    TournamentGameStats,
    ButtonTooltip,
    TournamentResultSelect,
    RecycleScroller,
    TournamentPairingPlayerSelect,
  },
  name: "AdminRoundTable",

  props: ["tournament", "rounds", "round", "games", "players", "elapsed"],

  mixins: [gameUrls, TournamentGameStatsLogic],

  data() {
    return {
      roundGames: [],
      isLoading: false,
      hoverId: null,
      allPlayers: null,
      playerCache: new Map(), // Cache player lookups
      resultMismatchCache: new Map(), // Add cache for result mismatches
      TOURNAMENT_GAME_RESULT,
    };
  },

  computed: {
    tournamentResultText() {
      return TOURNAMENT_GAME_RESULT_TEXT;
    },
    tournamentResultScore() {
      return TOURNAMENT_GAME_RESULT_SCORE;
    },

    tournamentRef() {
      return firestore.collection("tournaments").doc(this.tournament.id);
    },

    gamesRef() {
      return this.tournamentRef.collection("games");
    },

    playersRef() {
      return this.tournamentRef.collection("players");
    },

    sortedPlayers() {
      return this.memoizedSortPlayers(this.players, this.round);
    },

    unpairedPlayers() {
      if (!this.roundGames) return [];
      return this.memoizedGetUnpairedPlayers(
        this.roundGames,
        this.sortedPlayers
      );
    },

    availableUnpairedPlayers() {
      return this.unpairedPlayers.filter((p) => p.isParticipating);
    },

    playersStats() {
      if (!this.sortedPlayers) return null;
      return {
        total: this.sortedPlayers.length,
        absent: this.sortedPlayers.filter((p) => !p.isParticipating).length,
        paired: this.sortedPlayers.length - this.unpairedPlayers.length,
        available: this.availableUnpairedPlayers.length,
      };
    },
  },

  watch: {
    games: {
      immediate: true,
      handler() {
        this.roundGames = this.games
          .filter((g) => g.roundNumber === this.round.roundNumber)
          .sort((a, b) => a.board - b.board)
          .map((g) => ({ ...g }));
        if (!this.roundGames.length) {
          this.roundGames = initBoards(
            this.round.roundNumber,
            Math.ceil(this.players.length / 2)
          );
        }
      },
    },
    roundGames: {
      handler() {
        this.resultMismatchCache.clear();
      },
      deep: true,
    },
  },

  filters: {
    matchResultText(match) {
      if (!match) return "*no match*";
      if (!match.result && match.status)
        return "*" + match.status.toUpperCase() + "*";
      return (
        GameHelper.scoreText(match.player_1_score, match.player_2_score) +
        "|" +
        match.result
      );
    },
  },

  methods: {
    colorPref(player) {
      if (!this.round.standings || !this.round.standings.length) return "+0";

      const standing = this.round.standings.find((s) => {
        return s.player?.id ?? s.id === player.username;
      });

      if (!standing) {
        return "";
      }

      const standingPlayer = standing.player;
      const colors = getPlayedColors(standingPlayer);
      const colorPref = getPlayedColorPref(standingPlayer);

      return `[${colors.join("")}]${colorPref >= 0 ? "+" : ""}${colorPref}`;
    },

    findPlayer(username) {
      if (this.playerCache.has(username)) {
        return this.playerCache.get(username);
      }
      const player = this.sortedPlayers.find((p) => p.username === username);
      this.playerCache.set(username, player);
      return player;
    },

    addBoard() {
      this.roundGames.push(
        newBoard(this.round.roundNumber, this.roundGames.length + 1)
      );
    },

    async copyRound() {
      await navigator.clipboard.writeText(
        getJSONTournamentAtRound(
          this.tournament,
          this.players,
          this.games,
          this.rounds,
          this.round.roundNumber
        )
      );
      this.$buefy.toast.open({
        message: `Round state copied`,
        type: "is-info",
      });
    },

    async simulate() {
      await this.copyRound();
      this.$router.replace({ name: "AdminTournamentSimulation" });
    },

    abortRound() {
      this.$buefy.dialog.confirm({
        title: `Reset Round ${this.round.roundNumber}?`,
        message: `Be careful! This action would ABORT all games in current round.`,
        cancelText: "Cancel",
        confirmText: "Abort",
        type: "is-danger",
        trapFocus: true,
        onConfirm: () => {
          this.roundGames
            .filter((g) => g.matchRef)
            .forEach((g) => {
              firestore
                .collection("matches")
                .doc(g.matchGUID)
                .update({
                  status: "aborted",
                  result: "abort",
                  player_1_score: null,
                  player_2_score: null,
                  endDate: g.matchRef.endDate ? g.matchRef.endDate : new Date(),
                });
            });
          this.$buefy.toast.open({
            message: `round ${this.round.roundNumber} games were aborted.`,
            type: "is-danger",
          });
          this.$emit("close");
        },
      });
    },

    resetRound() {
      if (
        !this.roundGames ||
        this.roundGames.find(
          (g) => g.matchRef && g.matchRef.status !== "aborted"
        )
      ) {
        this.$buefy.toast.open({
          message: `There are still un-aborted games! ABORT all games in the round first`,
          type: "is-danger",
        });
        return;
      }

      this.$buefy.dialog.confirm({
        title: `Reset Round ${this.round.roundNumber}?`,
        message: `This action would unlink all existing round games from the tournament. All of them should have been aborted`,
        cancelText: "Cancel",
        confirmText: "Reset",
        type: "is-dark",
        trapFocus: true,
        onConfirm: () => {
          this.roundGames = initBoards(
            this.round.roundNumber,
            Math.ceil(this.players.length / 2)
          );
          this.$buefy.toast.open({
            message: `round ${this.round.roundNumber} games were reset. If this was an accident, DON'T SAVE`,
            type: "is-danger",
          });
        },
      });
    },

    handlePair() {
      if (!this.roundGames || this.roundGames.find((g) => g.matchRef)) {
        this.$buefy.toast.open({
          message: `There are round game(s) which have started. You have to RESET the round before re-pairing`,
          type: "is-danger",
        });
        return;
      }

      this.$buefy.dialog.confirm({
        title: `${this.tournament.type} pair Round ${this.round.roundNumber}`,
        message: `This action do nothing if there are exiting games. If that is the case, you have to ABORT those games first.`,
        cancelText: "Cancel",
        confirmText: "Pair",
        type: "is-primary",
        trapFocus: true,
        onConfirm: async () => {
          this.isLoading = true;
          const participatedPlayers = this.sortedPlayers.filter(
            (p) => p.isParticipating
          );

          const newGames = initBoards(
            this.round.roundNumber,
            Math.ceil(participatedPlayers.length / 2)
          );

          const roundPlayers = await getRoundPlayers(
            firestore,
            this.tournament,
            this.allPlayers,
            this.games,
            this.round
          );

          const standings = getStandings(
            this.tournament,
            this.games,
            roundPlayers,
            this.round.roundNumber + 1 // +1 to get the standings at end of this round which is start of next round
          );
          const possibleGames = await pairRound(
            {
              tournament: this.tournament,
              games: this.games.filter(
                (g) => g.roundNumber < this.round.roundNumber
              ),
              players: standings.map((s) => {
                return roundPlayers.find(
                  (p) => p.username === s.player?.id ?? s.id
                );
              }),
              round: this.round,
            },
            async (params) => {
              const response = await handleBBPPair(params);
              return response.data;
            }
          );

          if (!possibleGames.length) {
            this.$buefy.toast.open({
              message: "No valid pairings!",
              type: "is-danger",
            });
            this.isLoading = false;
            return;
          }

          newGames.forEach((g, idx) => {
            const game = possibleGames[idx];
            setGamePlayers(g, game?.whiteUsername, game?.blackUsername);
          });
          this.roundGames = [...newGames]; // need this to trigger rendering
          this.isLoading = false;
        },
      });
    },

    setGamePlayers(game, whiteUsername, blackUsername) {
      const cacheKey = `${game.board}-${game.roundNumber}`;
      this.resultMismatchCache.delete(cacheKey);

      setGamePlayers(game, whiteUsername, blackUsername);
      this.roundGames = [...this.roundGames];
    },

    getPlayerRef(username) {
      if (!username) return null;
      return this.playersRef.doc(username);
    },

    async saveRoundGame(game) {
      const cacheKey = `${game.board}-${game.roundNumber}`;
      this.resultMismatchCache.delete(cacheKey);

      this.isLoading = true;
      await saveRoundGame(
        firestore,
        this.tournament.id,
        this.round,
        game,
        this.games
      );
      this.isLoading = false;
    },

    async saveRound() {
      // Clear entire cache when saving round
      this.resultMismatchCache.clear();

      this.isLoading = true;
      await saveRound(
        firestore,
        this.tournament.id,
        this.round,
        this.roundGames,
        this.games
      );
      this.isLoading = false;
      this.$emit("onSave", this.round.roundNumber);
    },

    async startGame(game) {
      await this.saveRoundGame(game);
      const result = await startRoundGame(
        firestore,
        serverTimestamp(),
        this.tournament,
        game,
        this.players
      );

      if (result) {
        return;
      }
      const whitePlayer = this.players.find(
        (p) => p.username === game.whiteUsername
      );
      const blackPlayer = this.players.find(
        (p) => p.username === game.blackUsername
      );

      const matchId = await MatchHelper.createMatch({
        matchId: game.matchGUID,
        whitePlayer: whitePlayer.user,
        blackPlayer: blackPlayer.user,
        gameOverride:
          isAiPlayer(whitePlayer.user) || isAiPlayer(blackPlayer.user)
            ? GAME_OVERRIDE.PVAI
            : GAME_OVERRIDE.TOURNAMENT, // Tournament,
        duration: this.tournament.baseTimeControl * 60,
        increment: this.tournament.increment,
        variant: "standard",
        initialFen: this.tournament.initialFen,
        tournamentId: this.tournament.id,
        roundNumber: game.roundNumber,
        board: game.board,
      });

      if (!matchId) {
        console.log(
          this.tournament.id,
          game.roundNumber,
          "startGame",
          `Failed to start board ${game.board}`
        );
      }
    },

    async rematchGame(game) {
      this.$buefy.dialog.confirm({
        title: "Rematch Confirmation",
        message:
          "Are you sure you want to do a <b>rematch</b>? This would create a new match for the 2 players and overwrite the existing one.",
        confirmText: "Rematch",
        type: "is-danger",
        hasIcon: true,
        onConfirm: async () => {
          this.setGamePlayers(game, game.whiteUsername, game.blackUsername);
          game.matchRef = null;
          game.matchGUID = uuidv4();
          game.whiteResult = TOURNAMENT_GAME_RESULT.UNFINISHED;
          game.blackResult = TOURNAMENT_GAME_RESULT.UNFINISHED;
          game.startTime = null;
          game.finishTime = null;
          await this.saveRoundGame(game);
        },
      });
    },

    hasPlayerMismatch(game) {
      return GameHelper.hasPlayerMismatch(game);
    },

    hasResultMismatch(game) {
      if (!game) return false;

      const cacheKey = `${game.board}-${game.roundNumber}`;
      if (!this.resultMismatchCache.has(cacheKey)) {
        const result = hasResultMismatch(game, game.matchRef);
        this.resultMismatchCache.set(cacheKey, result);
      }
      return this.resultMismatchCache.get(cacheKey);
    },
  },

  beforeCreate() {
    // Memoize expensive functions
    this.memoizedSortPlayers = memoize((players, round) => {
      let sorted = players.map((p) => ({
        ...p,
        isParticipating: isPlayerInRound(p, round.roundNumber),
        scoreBeforeGame: getScoreBeforeGame(p.username, round),
        colorPref: this.colorPref(p),
      }));
      return sorted.sort((a, b) => b.scoreBeforeGame - a.scoreBeforeGame);
    });

    this.memoizedGetUnpairedPlayers = memoize((games, sortedPlayers) => {
      const pairedUsernames = new Set(
        games.flatMap((g) => [g.whiteUsername, g.blackUsername]).filter(Boolean)
      );
      return sortedPlayers.filter((p) => !pairedUsernames.has(p.username));
    });
  },

  async created() {
    await this.$bind("allPlayers", this.tournamentRef.collection("players"));
  },

  beforeDestroy() {
    this.playerCache.clear();
    this.resultMismatchCache.clear();
  },
};
</script>

<style scoped>
.roundTable {
  width: clamp(640px, 95vw, 1150px);
}

.scroller-table {
  height: 600px; /* Adjust based on your needs */
  width: 100%;
}

.table-row {
  display: flex;
  align-items: center;
  border-bottom: 1px solid #dbdbdb;
}

.table-cell {
  padding: 0.5em 0.75em;
}

/* Match the original table column widths */
.table-cell:nth-child(1) {
  width: 5%;
} /* Board */
.table-cell:nth-child(2) {
  width: 20%;
} /* Status */
.table-cell:nth-child(3) {
  width: 35%;
} /* White */
.table-cell:nth-child(4) {
  width: 5%;
} /* Swap */
.table-cell:nth-child(5) {
  width: 35%;
} /* Black */

.table-row:hover {
  background-color: #fafafa;
}
</style>
